import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, Users, Award, ArrowRight, Sparkles, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();

  const achievements = [
    { number: t("about.achievement1Value"), label: t("about.achievement1Label"), icon: Target },
    { number: t("about.achievement2Value"), label: t("about.achievement2Label"), icon: Award },
    { number: t("about.achievement3Value"), label: t("about.achievement3Label"), icon: Users },
    { number: t("about.achievement4Value"), label: t("about.achievement4Label"), icon: Heart }
  ];

  const values = [
    {
      title: t("about.value1Title"),
      description: t("about.value1Description"),
      icon: Sparkles
    },
    {
      title: t("about.value2Title"),
      description: t("about.value2Description"),
      icon: Star
    },
    {
      title: t("about.value3Title"),
      description: t("about.value3Description"),
      icon: Heart
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4ECDC4] via-[#4ECDC4] to-[#95C11F] opacity-90"></div>
        
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-white/15 rounded-full animate-bounce delay-300"></div>
          <div className="absolute bottom-32 left-1/4 w-16 h-16 bg-white/20 rounded-full animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-1/3 w-20 h-20 bg-white/10 rounded-full animate-pulse delay-500"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                  {t('about.heroTitle')} 
                  <span className="block bg-white text-transparent bg-clip-text">
                    {t('about.heroName')}
                  </span>
                </h1>
                <p className="text-xl lg:text-2xl opacity-90 leading-relaxed">
                  {t('about.heroSubtitle')}
                </p>
              </div>

              <div className="space-y-6 text-lg leading-relaxed">
                <p className="opacity-95">
                  {t('about.heroDescription1')}
                </p>
                <p className="opacity-95">
                  {t('about.heroDescription2')}
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/about#story" className="bg-white text-[#4ECDC4] px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center">
                  {t('about.myStory')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link to="/products" className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#4ECDC4] transition-all duration-300">
                  {t('about.viewProducts')}
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 bg-white/20 backdrop-blur-sm rounded-3xl p-8 border border-white/30">
                <div className="text-center space-y-6">
                  <div className="w-48 h-48 mx-auto bg-white/30 rounded-full flex items-center justify-center border-4 border-white/50">
                    <Heart className="w-24 h-24 text-white" />
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-white">{t('about.heroName')}</h3>
                    <p className="text-white/90 text-lg">
                      {t("about.quoteText")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl transform rotate-12 hover:rotate-0 transition-transform duration-300">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#4ECDC4]">{t('about.achievement1Value')}</div>
                  <div className="text-sm text-gray-600">{t('about.achievement1Label')}</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl transform -rotate-12 hover:rotate-0 transition-transform duration-300">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#95C11F]">{t('about.achievement2Value')}</div>
                  <div className="text-sm text-gray-600">{t('about.achievement2Label')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('about.journeyTitle')}
              <span className="bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] bg-clip-text text-transparent">
                {' '}{t('about.journeySubtitle')}
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.journeyDescription')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="text-center group cursor-pointer"
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border-2 border-transparent hover:border-[#4ECDC4]/20">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <achievement.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {achievement.number}
                  </div>
                  <div className="text-gray-600 font-medium">
                    {achievement.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  {t('about.transformationTitle')}
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] rounded-full"></div>
              </div>

              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  {t('about.transformationP1')}
                </p>
                <p>
                  {t('about.transformationP2')}
                </p>
                <p>
                  {t('about.transformationP3')}
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#4ECDC4]/10 to-[#95C11F]/10 rounded-2xl p-6 border-l-4 border-[#4ECDC4]">
                <p className="text-lg font-semibold text-gray-900 italic">
                  "{t('about.transformationQuote')}"
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-[#4ECDC4] to-[#95C11F] rounded-3xl p-8 text-white">
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold">{t('about.missionTitle')}</h3>
                  <p className="text-lg opacity-95">
                    {t('about.missionDescription')}
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                      <span>{t('about.missionItem1')}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                      <span>{t('about.missionItem2')}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                      <span>{t('about.missionItem3')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('about.valuesTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.valuesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group cursor-pointer"
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 h-full border-2 border-transparent hover:border-[#4ECDC4]/20">
                  <div className="text-center space-y-6">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <value.icon className="w-10 h-10 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900">
                      {value.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 right-20 w-24 h-24 bg-white/15 rounded-full animate-bounce delay-300"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              {t('about.ctaTitle')}
            </h2>
            
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('about.ctaSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products" className="bg-white text-[#4ECDC4] px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center">
                {t('about.ctaButton')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/contact" className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#4ECDC4] transition-all duration-300 text-center">
                {t('about.contactButton')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
